import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send,
  Activity,
  User,
  Loader2,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Plus,
  Copy,
  Check,
  StopCircle,
  Clock,
  BookOpen,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import ActionCard from './ActionCard';

const MEMBER_SUGGESTIONS = [
  'How does the task workflow work?',
  'How do I request leave?',
  'My hours this week',
  'What is Smart Idle Detection?',
  'My tasks in progress',
  'How to connect GitHub?',
];

const ADMIN_SUGGESTIONS = [
  'Who logged in today?',
  'Which project is lagging?',
  'Pending approvals',
  'Total hours this week',
  'Who is on leave?',
  'Resource utilization overview',
];

const getTimeOfDayGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const GREETING_LINES = [
  "What would you like to know?",
  "Shall we get started?",
  "What's on your mind?",
  "How can I help you today?",
  "What do you want to explore?",
  "Ready when you are.",
  "Ask me anything.",
  "What can I clarify for you?",
  "Let's dive in—what do you need?",
  "Curious about something? Fire away.",
];

const HelpChatbot = ({ onSwitchToDocs }) => {
  const { user } = useAuth();
  const [greetingLine] = useState(
    () => GREETING_LINES[Math.floor(Math.random() * GREETING_LINES.length)]
  );
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(true);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const messagesEndRef = useRef(null);
  const fullResponseRef = useRef('');
  const abortControllerRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const autoResizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }, []);

  useEffect(() => {
    autoResizeTextarea();
  }, [inputMessage, autoResizeTextarea]);

  const fetchConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${baseUrl}/api/chat/help/conversations`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (e) {
      console.error('Failed to fetch help conversations:', e);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const loadConversation = useCallback(async (convId) => {
    if (isLoading || isStreaming) return;
    if (selectedConvId === convId) return;
    setLoadingConversation(true);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${baseUrl}/api/chat/help/conversations/${convId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const conv = await res.json();
      const msgs = (conv.messages || []).map((m, i) => ({
        id: m.id || `msg-${convId}-${i}-${m.role}`,
        role: m.role,
        content: m.content || '',
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
        isError: false,
        isStreaming: false,
      }));
      setMessages(msgs);
      setConversationId(convId);
      setSelectedConvId(convId);
    } catch (e) {
      console.error('Failed to load conversation:', e);
    } finally {
      setLoadingConversation(false);
    }
  }, [isLoading, isStreaming, selectedConvId]);

  const sendMessage = useCallback(
    async (messageContent = null) => {
      const messageToSend = messageContent || inputMessage;
      if (!messageToSend.trim() || isLoading || isStreaming) return;

      const sanitizedMessage = messageToSend.trim().replace(/[<>]/g, '');

      const userMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: sanitizedMessage,
        timestamp: new Date(),
      };

      const aiMessageId = `ai-${Date.now()}`;
      const aiMessagePlaceholder = {
        id: aiMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage, aiMessagePlaceholder]);
      setInputMessage('');
      setIsLoading(true);
      setIsStreaming(true);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${baseUrl}/api/chat/help/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            message: sanitizedMessage,
            conversation_id: conversationId || undefined,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          if (response.status === 503) setAiAvailable(false);
          let detail = '';
          try {
            const body = await response.json();
            detail = body.detail || body.message || '';
          } catch (_) {}
          const msg = detail || (response.status === 503 ? 'Service unavailable' : `Request failed (${response.status})`);
          throw new Error(msg);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        fullResponseRef.current = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'chunk') {
                  fullResponseRef.current += data.content;
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === aiMessageId
                        ? { ...msg, content: fullResponseRef.current, isStreaming: true }
                        : msg
                    )
                  );
                } else if (data.type === 'complete') {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === aiMessageId
                        ? { ...msg, content: data.message, isStreaming: false }
                        : msg
                    )
                  );
                  if (data.conversation_id) {
                    setConversationId(data.conversation_id);
                    setSelectedConvId(data.conversation_id);
                    fetchConversations();
                  }
                } else if (data.type === 'action_proposed') {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === aiMessageId
                        ? {
                            ...msg,
                            content: data.action?.label
                              ? `I can do this for you — just confirm:`
                              : msg.content,
                            isStreaming: false,
                            action: data.action,
                            actionStatus: 'pending',
                            actionOriginalMessage: sanitizedMessage,
                          }
                        : msg
                    )
                  );
                  if (data.conversation_id) {
                    setConversationId(data.conversation_id);
                    setSelectedConvId(data.conversation_id);
                  }
                } else if (data.type === 'error') {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === aiMessageId
                        ? { ...msg, content: data.message, isError: true, isStreaming: false }
                        : msg
                    )
                  );
                }
              } catch (e) {
                console.error('Parse error:', e);
              }
            }
          }
        }
      } catch (error) {
        const errorMsg =
          error.name === 'AbortError'
            ? 'Request cancelled.'
            : error.message && error.message.length > 10
            ? error.message
            : 'Sorry, I encountered an error. Please try again.';

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === aiMessageId
              ? { ...msg, content: errorMsg, isError: true, isStreaming: false }
              : msg
          )
        );
        if (error.message === 'Service unavailable') setAiAvailable(false);
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    },
    [inputMessage, isLoading, isStreaming, conversationId, fetchConversations]
  );

  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const handleActionDecision = useCallback(
    async (messageId, action, decision) => {
      if (!action?.id) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, actionStatus: 'loading' } : m
        )
      );
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const originalMessage =
          messages.find((m) => m.id === messageId)?.actionOriginalMessage || '';
        const res = await fetch(`${baseUrl}/api/chat/help/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            action_id: action.id,
            decision,
            conversation_id: conversationId || undefined,
            original_message: originalMessage,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(body.detail || body.message || 'Request failed');
        }
        const ok = body.ok !== false;
        const isCancel = decision === 'cancel' || body.cancelled === true;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  actionStatus: isCancel ? 'cancelled' : ok ? 'confirmed' : 'failed',
                  actionResultMessage: body.message || '',
                }
              : m
          )
        );
        if (body.message) {
          setMessages((prev) => [
            ...prev,
            {
              id: (Date.now() + Math.random()).toString(),
              role: 'assistant',
              content: body.message,
              timestamp: new Date(),
            },
          ]);
        }
        fetchConversations();
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  actionStatus: 'failed',
                  actionResultMessage: err.message || 'Failed',
                }
              : m
          )
        );
      }
    },
    [messages, conversationId, fetchConversations]
  );

  const startNewChat = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMessages([]);
    setInputMessage('');
    setConversationId(null);
    setSelectedConvId(null);
    setIsLoading(false);
    setIsStreaming(false);
    setLoadingConversation(false);
  }, []);

  const deleteConversation = useCallback(
    async (e, convId) => {
      e.stopPropagation();
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${baseUrl}/api/chat/help/conversations/${convId}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          setConversations((prev) => prev.filter((c) => c.id !== convId));
          if (selectedConvId === convId) startNewChat();
        }
      } catch (e) {
        console.error('Failed to delete conversation:', e);
      }
    },
    [selectedConvId, startNewChat]
  );

  const handleExampleClick = useCallback(
    (question) => {
      if (!isLoading && !isStreaming) sendMessage(question);
    },
    [sendMessage, isLoading, isStreaming]
  );

  const copyToClipboard = useCallback(async (text, msgId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(msgId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (_) {}
  }, []);

  const formatDate = (d) => {
    if (!d) return '';
    const dt = typeof d === 'string' ? new Date(d) : d;
    const now = new Date();
    const diff = now - dt;
    if (diff < 86400000) return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return dt.toLocaleDateString([], { weekday: 'short' });
    return dt.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const suggestions = user?.role === 'admin' ? ADMIN_SUGGESTIONS : MEMBER_SUGGESTIONS;

  return (
    <div className="relative flex h-[calc(100vh-4rem)] lg:h-[calc(100vh-5rem)] w-full bg-white dark:bg-secondary-900 overflow-hidden">
      {/* Sidebar - Chat History */}
      <div
        className={`flex-shrink-0 flex flex-col border-r border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800 transition-all duration-300 ease-out ${
          sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'
        }`}
      >
        {sidebarOpen && (
          <>
            <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-200 dark:border-secondary-700">
              <span className="text-sm font-semibold text-secondary-700 dark:text-secondary-300">
                Chat History
              </span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 text-secondary-500 dark:text-secondary-400 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            </div>
            <div className="p-2">
              <button
                onClick={startNewChat}
                className="group w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-all duration-200 border border-primary-200 dark:border-primary-800 hover:shadow-sm"
              >
                <Plus className="h-4 w-4 group-hover:rotate-90 transition-transform duration-300" />
                New Chat
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-2 pb-2">
              {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 px-3">
                  <MessageSquare className="h-8 w-8 text-secondary-300 dark:text-secondary-600 mb-2" />
                  <p className="text-xs text-secondary-400 dark:text-secondary-500 text-center">
                    No conversations yet. Start a new chat!
                  </p>
                </div>
              ) : (
                <div className="space-y-0.5">
                  {conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => loadConversation(conv.id)}
                      className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                        selectedConvId === conv.id
                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'hover:bg-secondary-50 dark:hover:bg-secondary-700/50 text-secondary-700 dark:text-secondary-300'
                      } ${loadingConversation ? 'pointer-events-none opacity-70' : ''}`}
                    >
                      <MessageSquare className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate font-medium">{conv.title || 'Pulse AI'}</p>
                        <p className="text-[11px] opacity-60 truncate">
                          {formatDate(conv.last_message_time)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => deleteConversation(e, conv.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-all"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Toggle sidebar when collapsed */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-r-lg bg-secondary-100 dark:bg-secondary-800 border border-l-0 border-secondary-200 dark:border-secondary-700 hover:bg-secondary-200 dark:hover:bg-secondary-700 transition-colors text-secondary-500 dark:text-secondary-400"
          title="Open chat history"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}

      {/* Main Chat Area */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Top Bar */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-2.5 bg-white dark:bg-secondary-900 flex items-center justify-between">
          <div className="flex items-center gap-3 min-h-[1.25rem]">
            {messages.length > 0 ? (
              <span className="text-xs text-secondary-400 dark:text-secondary-500 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {messages.length} messages
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button
                onClick={startNewChat}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                New Chat
              </button>
            )}
            {onSwitchToDocs && (
              <button
                onClick={onSwitchToDocs}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-secondary-600 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-lg transition-colors"
              >
                <BookOpen className="h-3.5 w-3.5" />
                Docs
              </button>
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
          {loadingConversation ? (
            <div className="flex flex-col items-center justify-center h-full py-12 animate-fade-in">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/40 dark:to-primary-900/20 flex items-center justify-center mb-4 shadow-sm">
                <Loader2 className="h-6 w-6 text-primary-600 dark:text-primary-400 animate-spin" />
              </div>
              <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">Loading conversation...</p>
              <div className="flex gap-1 mt-3">
                <div className="w-1.5 h-1.5 bg-primary-400 dark:bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 bg-primary-400 dark:bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 bg-primary-400 dark:bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-8">
              <div className="help-icon-pulse w-14 h-14 rounded-2xl bg-primary-600 dark:bg-primary-500 flex items-center justify-center mb-5">
                <Activity className="h-7 w-7 text-white" strokeWidth={2.25} />
              </div>

              <h3 className="help-greeting-enter text-lg font-semibold text-secondary-900 dark:text-white mb-0.5">
                {getTimeOfDayGreeting()}, {user?.full_name?.split(' ')[0] || user?.username || 'there'}
              </h3>
              <p className="help-greeting-enter-delay text-secondary-400 dark:text-secondary-500 text-sm mb-6">
                {greetingLine}
              </p>

              <div className="help-greeting-enter-delay flex flex-wrap justify-center gap-2 max-w-lg">
                {suggestions.map((q, i) => (
                  <button
                    key={q}
                    onClick={() => handleExampleClick(q)}
                    disabled={isLoading || isStreaming}
                    className="help-pill-enter px-3.5 py-2 rounded-lg text-sm text-secondary-600 dark:text-secondary-300 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 hover:border-primary-300 dark:hover:border-primary-700 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div key={conversationId || 'new'} className="space-y-5 max-w-3xl mx-auto w-full">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`help-message-enter flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`flex gap-3 max-w-[85%] ${
                      msg.role === 'user' ? 'flex-row-reverse' : ''
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center ${
                        msg.role === 'user'
                          ? 'bg-primary-600 text-white'
                          : msg.isError
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                          : 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400'
                      }`}
                    >
                      {msg.role === 'user' ? (
                        <User className="h-4 w-4" />
                      ) : (
                        <Activity className="h-4 w-4" strokeWidth={2.25} />
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <div
                        className={`rounded-2xl px-4 py-3 ${
                          msg.role === 'user'
                            ? 'bg-primary-600 text-white'
                            : msg.isError
                            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                            : 'bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 text-secondary-800 dark:text-secondary-200'
                        }`}
                      >
                        {msg.role === 'user' ? (
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                        ) : (
                          <div className="prose prose-sm max-w-none dark:prose-invert [&_ul]:space-y-1 [&_li]:mb-0 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_h2]:text-base [&_h3]:text-sm [&_a]:text-primary-600 [&_a]:dark:text-primary-400 [&_code]:bg-secondary-100 [&_code]:dark:bg-secondary-700 [&_code]:text-secondary-800 [&_code]:dark:text-secondary-200 [&_code]:px-1 [&_code]:rounded [&_pre]:bg-secondary-50 [&_pre]:dark:bg-secondary-900 [&_pre]:rounded-lg [&_pre]:p-3">
                            {msg.content ? (
                              <>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                                {msg.isStreaming && (
                                  <span className="help-typing-dots inline-flex ml-1 gap-1 items-center">
                                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                                  </span>
                                )}
                              </>
                            ) : msg.isStreaming ? (
                              <div className="flex items-center gap-3 py-1">
                                <div className="flex gap-1">
                                  <div className="w-2 h-2 bg-primary-400 dark:bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                  <div className="w-2 h-2 bg-primary-400 dark:bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                  <div className="w-2 h-2 bg-primary-400 dark:bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                                <span className="help-searching-pulse text-sm text-secondary-500 dark:text-secondary-400">
                                  Thinking...
                                </span>
                              </div>
                            ) : null}
                            {msg.action && (
                              <ActionCard
                                action={msg.action}
                                status={msg.actionStatus || 'pending'}
                                resultMessage={msg.actionResultMessage}
                                onConfirm={(a) => handleActionDecision(msg.id, a, 'confirm')}
                                onCancel={(a) => handleActionDecision(msg.id, a, 'cancel')}
                              />
                            )}
                          </div>
                        )}
                      </div>
                      {/* Copy button for AI responses */}
                      {msg.role === 'assistant' && msg.content && !msg.isStreaming && !msg.isError && (
                        <div className="flex items-center gap-1 px-1 mt-0.5">
                          <button
                            onClick={() => copyToClipboard(msg.content, msg.id)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] transition-all duration-200 ${
                              copiedId === msg.id
                                ? 'help-copy-success text-green-600 dark:text-green-400'
                                : 'text-secondary-400 dark:text-secondary-500 hover:text-secondary-600 dark:hover:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700/50'
                            }`}
                            title="Copy response"
                          >
                            {copiedId === msg.id ? (
                              <>
                                <Check className="h-3 w-3" />
                                <span>Copied!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="h-3 w-3" />
                                <span>Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800">
          {!aiAvailable && (
            <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-200 animate-fade-in">
              AI is currently unavailable. Try again later or browse the documentation.
            </div>
          )}
          <div className="relative flex items-end gap-3">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Ask anything..."
                disabled={isLoading || isStreaming || !aiAvailable}
                rows={1}
                className="help-input-glow w-full px-4 py-3 pr-20 rounded-xl border border-secondary-300 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100 placeholder:text-secondary-400 dark:placeholder:text-secondary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 resize-none overflow-hidden text-sm leading-relaxed"
              />
              {!isLoading && !isStreaming && (
                <span className="absolute right-3 bottom-2 text-[10px] text-secondary-300 dark:text-secondary-600 pointer-events-none select-none">
                  Shift+Enter for new line
                </span>
              )}
            </div>
            {isLoading || isStreaming ? (
              <button
                onClick={stopGeneration}
                className="help-stop-pulse flex-shrink-0 px-4 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium transition-all duration-200 flex items-center gap-2"
                title="Stop generating"
              >
                <StopCircle className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => sendMessage()}
                disabled={!inputMessage.trim() || !aiAvailable}
                className="flex-shrink-0 px-4 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 active:scale-95 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 hover:shadow-lg hover:shadow-primary-500/25"
              >
                <Send className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpChatbot;
